import { Box, Flex, Progress, Tooltip } from '@mantine/core';
import { Fragment, type ReactNode, useState } from 'react';
import { sumBy } from 'remeda';

import { type PaidByReminderSegment, segmentKey } from './paid-by-reminder-segments';

const HOVER_RING = '0 0 0 2px #fff, 0 4px 12px rgba(32, 10, 66, 0.22)';
const RING_TRANSITION = 'box-shadow 180ms cubic-bezier(.2, .7, .2, 1)';

// A white readout on a white card needs its own lift; Tooltip takes no shadow prop.
const READOUT_SHADOW = { boxShadow: 'var(--mantine-shadow-lg)' } as const;

const STILL_OPEN_STRIPES =
  'repeating-linear-gradient(-45deg, var(--mantine-color-coral-1) 0 4px, var(--mantine-color-coral-2) 4px 8px)';

type PaidByReminderBarProps = {
  segments: ReadonlyArray<PaidByReminderSegment>;
  onHoveredKeyChange?: (key: string | null) => void;
  tooltipFor?: (segment: PaidByReminderSegment) => ReactNode;
};

export const PaidByReminderBar = ({
  segments,
  onHoveredKeyChange,
  tooltipFor,
}: PaidByReminderBarProps) => {
  const [ringedKey, setRingedKey] = useState<string | null>(null);

  const hover = (key: string | null) => {
    setRingedKey(key);
    onHoveredKeyChange?.(key);
  };

  const settled = segments.filter(segment => segment.kind === 'step');
  const stillOpen = segments.filter(segment => segment.kind === 'open' && segment.share > 0);
  const settledShare = sumBy(settled, ({ share }) => share);

  const pointedAt = (segment: PaidByReminderSegment) => ({
    'aria-label': segment.label,
    onMouseEnter: () => hover(segmentKey(segment)),
  });

  const ringOf = (segment: PaidByReminderSegment) => ({
    boxShadow: segmentKey(segment) === ringedKey ? HOVER_RING : 'none',
    transition: RING_TRANSITION,
  });

  const withReadout = (segment: PaidByReminderSegment, bucket: ReactNode) => {
    if (tooltipFor === undefined) {
      return <Fragment key={segmentKey(segment)}>{bucket}</Fragment>;
    }

    return (
      <Tooltip
        key={segmentKey(segment)}
        label={tooltipFor(segment)}
        position="top"
        color="white"
        c="gray.8"
        radius={10}
        p="space4"
        maw={420}
        multiline
        styles={{ tooltip: READOUT_SHADOW }}
        transitionProps={{ duration: 120 }}
      >
        {bucket}
      </Tooltip>
    );
  };

  return (
    <Flex gap={6} onMouseLeave={() => hover(null)}>
      <Progress.Root
        size={24}
        style={{ flex: settledShare, gap: 2, overflow: 'visible', backgroundColor: 'transparent' }}
      >
        {settled.map(segment =>
          withReadout(
            segment,
            <Progress.Section
              value={(segment.share / settledShare) * 100}
              color={segment.color}
              withAria={false}
              {...pointedAt(segment)}
              style={{
                minWidth: 3,
                borderRadius: 5,
                ...ringOf(segment),
              }}
            />,
          ),
        )}
      </Progress.Root>
      {stillOpen.map(segment =>
        withReadout(
          segment,
          <Box
            h={24}
            {...pointedAt(segment)}
            style={{
              flex: segment.share,
              borderRadius: 5,
              border: '1px dashed var(--mantine-color-coral-6)',
              background: STILL_OPEN_STRIPES,
              ...ringOf(segment),
            }}
          />,
        ),
      )}
    </Flex>
  );
};
